/**
 * Feed Processing Module Types
 *
 * Contains API-aligned types (prefixed with Api*) returned by the
 * feed-processing service and view-model types consumed by UI components.
 */
import {
  FeedOrderTypeSchema,
  FeedOrderBaseSchema,
  FeedOrderRouteSchema,
  FeedFrequencySchema,
  type FeedOrderType,
  type CalculateFeed,
  type CalculateFeedResponse,
  type FeedMeasure,
  type ReservationSummary,
  type FeedPrepSplitSec,
} from '@tms-phoenix/tms-types';

// ---- Tab view ---------------------------------------------------------------
export type FeedTabView = 'orders' | 'feeds';

// ---- Domain enums -----------------------------------------------------------
/** Alias of the canonical FeedOrderType from @tms-phoenix/tms-types. */
export type OrderType = FeedOrderType;
export type OrderStatus = 'ACTIVE' | 'INACTIVE' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD' | 'PENDING';

// ---- API types (mirrors backend JSON) --------------------------------------
export type ApiOrderListItem = {
  order_id: string;
  order_identifier: string | null;
  encounter_id: string;
  patient_id: string;
  hospital_id: string;
  ordered_by: string;
  transcribed_by: string | null;
  order_base: string;
  order_type: OrderType;
  recipe_id: string | null;
  volume_ml_per_feed: string | null;
  calorific_density: string | null;
  feed_frequency: string | null;
  route: string | null;
  milk_type: string | null;
  special_instructions: string | null;
  start_date: string;
  end_date: string | null;
  status: OrderStatus;
  // Why this order was cancelled — system-generated for edit/reorder, user-typed for an
  // explicit remove or panel-staged Cancel. Never special_instructions (see feedOrder.repo.ts deactivate()).
  cancellation_reason: string | null;
  // The order this row supersedes (edit/reorder), or null for a plain create and for
  // panel-staged Cancels (the new order is not their replacement).
  replaces_order_id: string | null;
  created_at: string;
  updated_at: string;
  patient: {
    first_name: string;
    last_name: string;
    mrn: string;
  } | null;
  feed_order_items?: Array<{
    product_id: string;
    quantity: number;
    quantity_unit: string;
    local_name?: string | null;
    brand_name?: string | null;
    form_name?: string | null;
    sku?: string | null;
  }> | null;
};

export type ApiOrderDetail = ApiOrderListItem & {
  order_has_feeds: boolean;
  warnings: Array<{
    allergen_id: string;
    allergen_code: string;
    allergen_name: string;
    product_id: string;
    product_name: string;
    system_action: string;
  }> | null;
  // GET /feed-orders/:order_id returns additives under `items` (FeedOrderDetailSchema), not
  // `feed_order_items` (that's the list-item shape, FeedOrderListItemSchema) — a different field
  // name for the same kind of row. Always read `items` on an ApiOrderDetail.
  items: Array<{
    item_id: string;
    product_id: string;
    quantity: number;
    quantity_unit: string;
    notes?: string | null;
    local_name?: string | null;
    brand_name?: string | null;
    form_name?: string | null;
    sku?: string | null;
  }> | null;
};

export type ApiResponse<T> = {
  success: boolean;
  data: T;
  meta: {
    requestId: string;
    timestamp: string;
    pagination?: {
      totalItems: number;
      currentPage: number;
      itemsPerPage: number;
      hasNextPage: boolean;
    };
  };
};

// ---- View-model types (consumed by components) -----------------------------
export type FeedOrder = {
  id: string;
  orderIdentifier: string | null;
  patientId: string;
  patientName: string;
  patientFirstName: string;
  patientLastName: string;
  patientMrn: string;
  orderType: OrderType;
  orderValidFrom: string;
  orderBase: string;
  feedMethod: string;
  volumeMl: number | null;
  feedFrequency: string;
  milkType?: string | null;
  caloricDensity?: number | null;
  items?: Array<{
    productId: string;
    quantity: number;
    quantityUnit: string;
    localName?: string | null;
    brandName?: string | null;
    formName?: string | null;
    sku?: string | null;
  }> | null;
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
};

// ---- Request shapes ---------------------------------------------------------
export type CreateOrderRequest = {
  original_order_id?: string;
  // Discriminates an edit() call into a plain field-edit vs. a reorder — isMinorChange can't be
  // used for this (a no-field-change reorder still computes as minor). REORDER forces the
  // backend to ignore start_date and use "now" instead; the picker is disabled client-side to
  // match. Defaults to 'EDIT' server-side when omitted.
  order_action?: 'EDIT' | 'REORDER';
  encounter_id: string;
  patient_id: string;
  hospital_id: string;
  ordered_by: string;
  order_base: string;
  order_type: OrderType;
  recipe_id?: string | null;
  volume_ml_per_feed: number;
  calorific_density: number;
  feed_frequency: string;
  route: string;
  milk_type: string;
  special_instructions?: string;
  start_date: string;
  items?: Array<{ product_id: string; quantity: number; quantity_unit: string }>;
  // Orders to cancel alongside this create/edit — panel-staged Cancels, each with its own
  // optional clinician-typed reason (documenting one is the hospital's choice, not mandatory).
  // Every id is validated server-side (patient_id/hospital_id/status match) before any write.
  // Max 20.
  cancel_order_ids?: Array<{ order_id: string; reason?: string }>;
};

export type UpdateOrderRequest = Partial<CreateOrderRequest>;

// ---- Feeds tab view-model types ----------------------------------------
/** Canonical statuses from FeedListItemSchema (tms-types) + legacy display values. */
export type FeedStatus = 'DUE' | 'PENDING' | 'COMPLETED' | 'OVERDUE' | 'ON_TIME' | 'DUE_SOON' | 'SEND_HOME';

export type ApiFeedListItem = {
  feed_id: string;
  patient_id: string;
  patient_name: string;
  patient_mrn: string;
  due_time: string;
  order_description: string;
  status: FeedStatus;
};

/** API response item shape from GET /feedPrep/feeds */
export type ApiFeedPrepListItem = {
  schedule_id: string;
  order_id: string;
  order_identifier: string | null;
  patient_id: string;
  hospital_id: string;
  prep_id: string;
  interval_minutes: number;
  volume_ml_per_feed: string;
  start_time: string;
  end_time: string | null;
  is_active: boolean;
  display_status: string;
  patient_name: string;
  mrn: string;
  order_base: string | null;
  route: string | null;
  created_at: string;
};

/** Paginated response envelope from GET /feedPrep/feeds */
export type ApiFeedPrepListResponse = {
  items: ApiFeedPrepListItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
};

export type FeedItem = {
  id: string;          // schedule_id from /feedPrep/feeds
  prepId: string;      // prep_id — underlying preparation
  splitId: string | null; // split_id — target for split-level deactivation (Cancel action)
  patientId: string;
  patientName: string;
  patientFirstName: string;
  patientLastName: string;
  patientMrn: string;
  dueTime: string;
  orderDate: string | null;
  volumeMl: string | null;
  orderDescription: string;
  orderIdentifier?: string | null;
  /** FeedStatus for the planned dedicated /feeds endpoint; OrderStatus when sourced from /feedOrders. */
  status: FeedStatus | OrderStatus;
};



// ---- Feed Prep: Scan (GET /feedPrep/scan) ----------------------------------

/** Single additive nested inside an ApiScanResult. */
export type ApiScanResultAdditive = {
  product_id: string;
  brand_name: string;
  quantity: string;
  quantity_unit: string;
  /**
   * Product SKU. A bare catalog SKU (e.g. "ABC123") identifies the product
   * family; a unit-level SKU carries a per-unit sequence suffix
   * (e.g. "ABC123-001", "ABC123-002") and identifies a specific Product Unit.
   * Used by classifyScanForRecipeCarryover() in PrepareFeedView to detect
   * Product Unit ID scans. Optional/nullable because older backend responses
   * may omit it.
   */
  sku?: string | null;
};

/** Linked mother returned in GET /feedPrep/scan. */
export type ApiLinkedMother = {
  person_id: string;
  first_name: string | null;
  last_name: string | null;
  relationship_type: string | null;
  provides_milk: boolean;
  available_ml: number;
};

/** Preferred donor preference returned in GET /feedPrep/scan. */
export type ApiPreferredDonor = {
  donor_id: string;
  first_name: string | null;
  last_name: string | null;
  priority_order: number;
  available_ml: number;
};

/** Wire-format item returned by GET /feedPrep/scan. */
export type ApiScanResult = {
  order_id: string;
  /** The patient's UUID — used for product safety checks (allergen, recall, expiry). */
  patient_id: string;
  patient_name: string;
  mrn: string;
  /** Mapped from order_base by the backend; matches FeedPrepScanResultSchema. */
  base: string;
  additives: ApiScanResultAdditive[];
  volume_per_feed_ml: string | null;
  feeds_per_day: number | null;
  total_ml_per_day: string | null;
  calorific_density: string | null;
  order_type: string;
  route: string | null;
  status: string;
  start_date: string | null;
  linked_mothers?: ApiLinkedMother[] | null;
  preferred_donors?: ApiPreferredDonor[] | null;
};

// ---- Feed Prep: Prepare (POST /feedPrep/prepare) ----------------------------

export type StartFeedPrepItem = {
  product_id?: string;
  unit_id?: string;
  milk_inventory_id?: string;
  receivable_id?: string;
  remaining_quantity: number; // post-prep remaining; server derives quantity_used = available − remaining_quantity
  quantity_unit: string;
  scanned_barcode?: string;
  /** "Restore To" location selected in the UI for this item's remaining quantity.
   * inventory_locations id for product units, storage_locations id for milk items.
   * Omitted when the row is locked/disposed (nothing left to restore). */
  restore_location_id?: string;
};

export type StartFeedPrepSplit = {
  volume_ml: number;
  storage_location_id?: string;
  expiry_date?: string;
  is_restored?: boolean;
  restored_from_split_id?: string;
};

export type StartFeedPrepBody = {
  hospital_id: string;
  order_id: string;
  prepared_by: string;
  desired_volume_ml: number;
  route?: string;
  items: StartFeedPrepItem[];
  milk_source_type?: string;
  deviation_reason?: string;
  donor_id?: string;
  splits: StartFeedPrepSplit[];
  remainder_action?: 'DISCARD' | 'STORE';
  remainder_storage_location_id?: string;
  remainder_wasted_reason?: string;
};

export type ApiPrepareItem = {
  product_id: string;
  product_name: string;
  available_volume: string;
  quantity_used: string;
  quantity_unit: string;
  caloric_value: string | null;
  restore_to: string;
};

export type ApiPrepareSplit = {
  /** Present on GET /feedPrep/:prep_id and GET /feedPrep/container/:id responses. */
  split_id?: string;
  split_sequence: number;
  /** Present on GET /feedPrep/:prep_id and POST /feedPrep/prepare responses. */
  container_identifier?: string | null;
  syringe_label_barcode?: string | null;
  volume_ml: string;
  status: string;
  /** Nullable — may be null when no expiry is set. */
  label_expiry_at: string | null;
  is_expired: boolean;
  expiry_notes: string | null;
  is_restored: boolean;
  restored_from_split_id: string | null;
  /** Origin: 'NORMAL_SPLIT' (user-defined container) or 'RESTORE_REMAINDER' (leftover prepared feed). */
  inventory_source_type?: 'NORMAL_SPLIT' | 'RESTORE_REMAINDER';
  /** Present on GET /feedPrep/:prep_id response. */
  delivery_id?: string | null;
  /** Present on GET /feedPrep/container/:id response. */
  destination_patient_id?: string | null;
  destination_patient_name?: string | null;
  destination_mrn?: string | null;
  sec?: FeedPrepSplitSec;
};

export type ApiPrepareResponse = {
  prep_id: string;
  order_id: string;
  patient_name: string;
  mrn: string;
  status: string;
  desired_volume_ml: string;
  items: ApiPrepareItem[];
  splits: ApiPrepareSplit[];
  remainder_ml: string;
  remainder_action: string | null;
  /** Container identifier of the auto-generated STORE/DISCARD remainder split, if any. */
  remainder_container_identifier?: string | null;
  warnings: string[];
};

/** A single stored/remaining feed split, as returned by GET /feedPrep/stored. */
export type ApiStoredSplit = {
  split_id: string;
  prep_id: string;
  split_sequence: number;
  container_identifier: string | null;
  volume_ml: string;
  syringe_label_barcode: string | null;
  label_expiry_at: string | null;
  storage_location_id: string | null;
  is_expired: boolean;
  expiry_notes: string | null;
  status: string;
  patient_name: string;
  mrn: string;
  order_id: string | null;
  order_base: string | null;
  created_at: string;
};

/** Response payload (already unwrapped from ApiResponse) for GET /feedPrep/stored. */
export type ApiStoredSplitsResponse = {
  items: ApiStoredSplit[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  warnings: Array<{ type: string; message: string }>;
};

/** Response from GET /feedPrep/scan-item/:barcode */
export type ApiScanItemResponse = {
  source: 'MILK_INVENTORY' | 'PRODUCT_INVENTORY' | 'MILK_BATCH' | 'FEED_PREP' | 'MILK_RECEIVABLE';
  identifier: string;
  hospital_id: string;
  status: string;
  expiry_at: string | null;
  milk_inventory_id: string | null;
  container_barcode: string | null;
  container_type: string | null;
  initial_volume_ml: string | null;
  remaining_volume_ml: string | null;
  storage_location_id: string | null;
  batch_id: string | null;
  batch_number: string | null;
  receivable_id: string | null;
  receipt_number: string | null;
  source_type: string | null;
  milk_type: string | null;
  provider_id: string | null;
  provider_name: string | null;
  unit_id: string | null;
  product_id: string | null;
  sku: string | null;
  tms_uuid: string | null;
  lot_number: string | null;
  original_quantity: string | null;
  remaining_quantity: string | null;
  available_quantity: string | null;
  unit: string | null;
  unit_status: string | null;
  milk_batch_id: string | null;
  volume_ml: string | null;
  batch_status: string | null;
  prep_id: string | null;
  container_identifier: string | null;
  prep_type: string | null;
  patient_id: string | null;
  patient_name: string | null;
  prepared_by: string | null;
  actual_volume_ml: string | null;
  completed_at: string | null;
};

/** Response from GET /feedPrep/container/:container_identifier */
export type ApiContainerInfo = {
  prep_id: string;
  container_identifier: string;
  prep_type: string;
  status: string;
  patient_id: string | null;
  patient_name: string | null;
  mrn: string | null;
  hospital_id: string;
  prepared_by: string;
  actual_volume_ml: string;
  route: string | null;
  order_id: string | null;
  order_type: string | null;
  allergy_check_passed: boolean;
  expiry_check_passed: boolean;
  recall_check_passed: boolean;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  splits: ApiPrepareSplit[];
  items: ApiPrepareItem[];
  mother_id: string | null;
  mother_name: string | null;
  mother_mrn: string | null;
  date_of_birth: string | null;
  unit: string | null;
  bed: string | null;
};

// ---- Prepare Feed view-model -----------------------------------------------

export type EligiblePatient = {
  /** Feed order ID from ApiScanResult.order_id — used as the selection key. */
  orderId: string;
  /** Same as orderId; kept so DataTable row-key="patientId" works unchanged. */
  patientId: string;
  /** The patient's actual UUID — passed to scan-item for product safety checks. */
  patientUuid: string;
  patientName: string;
  mrn: string;
  base: string;
  orderType: string;
  additives: ApiScanResultAdditive[];
  volFeedMl: number;
  feedsPerDay: number;
  totalMl: number;
  route: string | null;
  calorificDensity: number;
  /** Mothers linked to this patient's order — used for milk source validation. */
  linkedMothers: ApiLinkedMother[];
  /** Preferred donor preferences for this patient's order — used for milk source validation. */
  preferredDonors: ApiPreferredDonor[];
};

export type RecipeIngredient = {
  /** Stable row identity — set at creation time via crypto.randomUUID(). */
  id: string;
  barcode?: string;
  product_id?: string;
  /** inventory_units PK — set from scan response; enables backend deduction for product items. */
  unit_id?: string;
  /** milk_inventory PK — set from scan response; enables backend deduction for milk items. */
  milk_inventory_id?: string;
  /** milk_receivables PK — fallback when no milk_inventory exists yet (e.g. use_type=FEED receivables). */
  receivable_id?: string;
  /** True when the ingredient was scanned as Mother's Own Milk (set at scan time). */
  isMomMilk?: boolean;
  name: string;
  availableVolume: string;
  requiredQuantity: string;
  usingQuantity: string;
  quantity_unit: string;
  restoreTo: string;
  /** The ingredient's actual current storage location (e.g. where the bottle/unit was
   * scanned from). Used to restore the Restore To selection to its real location when
   * Remaining Quantity goes from 0 back to > 0, instead of leaving it stuck on Dispose. */
  currentLocationId?: string;
  /** True when Restore To was auto-set to 'DISPOSE' by the Remaining-Quantity-locked rule
   * (as opposed to the user manually choosing Dispose). Cleared on manual selection. */
  restoreToAutoDisposed?: boolean;
  requiresVerification?: boolean;
  isVerified?: boolean;
};

export type PreppedContainer = {
  containerNumber: number;
  preppedVolumeMl: string;
  labelNote: string;
  expiryDate: string;
  /** Optional storage location UUID sent to /feedPrep/prepare splits. */
  storage_location_id?: string;
  /** Whether this container is restored from a previously stored split. */
  is_restored?: boolean;
  /** UUID of the stored split this container is restored from. */
  restored_from_split_id?: string;
};

// ---- Option lists (derived from authoritative Zod enums in @tms-phoenix/tms-types) --------

/** i18n keys for Order Type card labels and descriptions — UI-only. */
const ORDER_TYPE_LABEL_KEYS: Record<string, string> = {
  Standard: 'feed.typeStandard',
  NPO: 'feed.typeNpo',
  'Oral Care': 'feed.typeOralCare',
};
const ORDER_TYPE_DESCRIPTION_KEYS: Record<string, string> = {
  Standard: 'feed.orderType.standard.description',
  NPO: 'feed.orderType.npo.description',
  'Oral Care': 'feed.orderType.oralCare.description',
};

export const ORDER_TYPE_OPTIONS = FeedOrderTypeSchema.options.map((v) => ({
  value: v as OrderType,
  label: v,
  labelKey: ORDER_TYPE_LABEL_KEYS[v] ?? '',
  descriptionKey: ORDER_TYPE_DESCRIPTION_KEYS[v] ?? '',
}));

const ORDER_BASE_LABEL_KEYS: Record<string, string> = {
  "Mother's Milk Only": 'feed.orderBase.mothersMilkOnly',
  'Donor Milk': 'milk.donorMilk',
  'Nutrition Product/Formula': 'feed.orderBase.nutritionFormula',
  'Mixed (MOM + Donor fallback)': 'feed.orderBase.mixed',
};

export const ORDER_BASE_OPTIONS = FeedOrderBaseSchema.options.map((v) => ({
  value: v,
  label: v,
  labelKey: ORDER_BASE_LABEL_KEYS[v] ?? '',
}));

const ROUTE_LABEL_KEYS: Record<string, string> = {
  'SNG (Nasogastric)': 'feed.route.sng',
  'VO (Oral/Per Os)':  'feed.route.vo',
  'VGNS (Nasogastric)':'feed.route.vgns',
  'OG (Orogastric)':   'feed.route.og',
  'PEG':               'feed.route.peg',
  'Gavage':            'feed.route.gavage',
};

export const ROUTE_OPTIONS = FeedOrderRouteSchema.options.map((v) => ({
  value: v,
  label: v,
  labelKey: ROUTE_LABEL_KEYS[v] ?? '',
}));

const FEED_FREQUENCY_LABEL_KEYS: Record<string, string> = {
  'Every 2h': 'feed.frequency.every2h',
  'Every 3h': 'feed.frequency.every3h',
  'Every 4h': 'feed.frequency.every4h',
  'Every 6h': 'feed.frequency.every6h',
};

export const FEED_FREQUENCY_OPTIONS = FeedFrequencySchema.options.map((v) => ({
  value: v,
  label: v,
  labelKey: FEED_FREQUENCY_LABEL_KEYS[v] ?? '',
}));

// ---- Feed Calculator (POST /feedCalculator/) --------------------------------
// Canonical shapes live in @tms-phoenix/tms-types — re-exported under the
// names already in use across the module so call-sites need no changes.

/** @see CalculateFeed in @tms-phoenix/tms-types */
export type FeedCalculatorRequest = CalculateFeed;
/** @see FeedMeasure in @tms-phoenix/tms-types */
export type FeedCalculatorMeasure = FeedMeasure;
/** @see ReservationSummary in @tms-phoenix/tms-types */
export type FeedCalculatorReservation = ReservationSummary;
/** @see CalculateFeedResponse in @tms-phoenix/tms-types */
export type FeedCalculatorResponse = CalculateFeedResponse;

export const FORTIFIER_OPTIONS = [
  { value: 'HMF', label: 'Human Milk Fortifier (HMF)' },
  { value: 'PDF', label: 'Protein/Calorie Supplement (PDF)' },
  { value: 'VITAMIN_D', label: 'Vitamin D' },
] as const;
